import { DateTime } from 'luxon';

import { AttributeInvalidError, AttributeRequiredError, DateUtil, hasFlag } from '../common';
import { NestedInputComponent } from '../nested-input';

const ATTR_BASE_DATE = 'base-date';
const ATTR_TIME_ZONE = 'time-zone';

const DATA_LUXON_VALUE = 'luxon-value';
const DATA_LUXON_ORIGINAL_VALUE = 'luxon-original-value';

const VALIDITY_INVALID_DATETIME = 'invalidDateTimeInput';

export interface TemporalInputElement extends HTMLElement {
  // placeholder for now
}

export enum SyncValueAttribute {
  none = 1,
  host = 2,
  value = 4,
  hostAndValue = SyncValueAttribute.host | SyncValueAttribute.value,
}

export abstract class TemporalComponent<TElement extends TemporalInputElement = TemporalInputElement> extends NestedInputComponent<TElement, DateTime> {
  /**
   * Used to optionally set the base date used when constructing a new date/time value from the selected time.
   */
  public get baseDate(): string {
    return this.$el.attr(ATTR_BASE_DATE);
  }

  public get timeZone(): string {
    return this.$el.attr(ATTR_TIME_ZONE);
  }

  public get isValid(): boolean {
    return this.valueEl.validity?.valid || false;
  }

  /**
   * Returns the Luxon {@link DateTime} object representation of the input's value.
   */
  public get luxonValue(): DateTime {
    return this.$el.data(DATA_LUXON_VALUE);
  }

  public get luxonOriginalValue(): DateTime {
    return this.$el.data(DATA_LUXON_ORIGINAL_VALUE);
  }

  public get isModified(): boolean {
    return this.luxonOriginalValue?.valueOf() !== this.luxonValue?.valueOf();
  }

  public get value(): string {
    return this.$value.val() as string;
  }
  public set value(value: string) {
    this.importValue(value);
  }

  /**
   * Determines which elements get their "value" attribute updated when the value changes
   */
  protected get syncValueAttr(): SyncValueAttribute { return SyncValueAttribute.hostAndValue };

  // note: this is required to ensure that inheritance works correctly
  protected constructor($el: JQuery<TElement>) {
    super($el);
  }

  /**
   * Returns a {@link DateTime} parsed from the provided input. Returns `undefined` if the input is a `null`,
   * `undefined`, or empty string value.
   *
   * @param input - A string representation of a date/time
   */
  public parseValue(input: string): DateTime {
    if (!input) {
      return undefined;
    }
    return DateUtil.tryParseDateTime(input, {
      baseDate: this.getBaseDate(),
      timeZone: this.timeZone,
    });
  }

  /**
   * Attempts to parse the specified input into a {@link DateTime} value and set it as the value for the component.
   */
  public importValue(input: string): DateTime {
    const value = this.parseValue(input);
    return this.syncValue(value);
  }

  /**
   * Convenience method for handling all logic around value updates: storing the parsed {@link DateTime} value, setting
   * DOM attribute and value properties, emitting change events, validation
   */
  public syncValue(value?: DateTime): DateTime {
    const prevValue = this.luxonValue;

    const isChanged = this.isValueChanged(prevValue, value);
    if (isChanged) {
      // called before updateValidityState so that the value can be displayed correctly, even though if it is invalid
      // invalid can be a valid date/time, but not valid for the input due to validation constraints (see min/max/step
      // in TimeSelectorComponent)
      this.setComponentValue(value);
      value = this.updateValidityState(value);
      if (value?.isValid) {
        this.valueEl.setCustomValidity('');
      }
      // called after updateValidityState so that invalid value is exposed via the luxonValue property
      this.setLuxonValue(value);
    }
    this.onValue(value, isChanged, this.isValid);

    return value;
  }

  /**
   * Stores the {@link DateTime} representation of the value for access by {@link luxonValue}
   */
  protected setLuxonValue(value: DateTime): void {
    this.$el.data(DATA_LUXON_VALUE, value);
  }

  /**
   * Stores the {@link DateTime} representation of the original value for access by {@link luxonOriginalValue}
   */
  protected setLuxonOriginalValue(value: DateTime): void {
    this.$el.data(DATA_LUXON_ORIGINAL_VALUE, value);
  }

  protected initValue(): void {
    super.initValue();
    this.importValue(this.originalValue);
    if (this.luxonValue) {
      this.setLuxonOriginalValue(this.luxonValue);
    }
  }

  protected getBaseDate(): DateTime {
    const baseDateSrc = this.baseDate || this.originalValue;
    const baseDate = baseDateSrc ? DateUtil.tryParseDateTime(baseDateSrc) : DateTime.local();
    if (this.timeZone) {
      return baseDate.setZone(this.timeZone);
    }
    return baseDate;
  }

  protected isValueChanged(prev: DateTime, next: DateTime): boolean {
    const changedFromFalsy = !prev && !!next;
    const changedToFalsy = !!prev && !next;
    const valueChanged = !!prev && !!next && !prev.equals(next);
    return changedFromFalsy || changedToFalsy || valueChanged;
  }

  protected setComponentValue(value: DateTime): void {
    const formattedValue = this.formatValue(value);
    const syncValueEl = hasFlag(this.syncValueAttr, SyncValueAttribute.value);
    if (syncValueEl) {
      this.$value.val(formattedValue); // for the backing hidden form input value
    }
    const hostAndValueAreDifferentElements = this.valueEl as HTMLElement !== this.el as HTMLElement;
    const syncHostEl = hasFlag(this.syncValueAttr, SyncValueAttribute.host)
    if (syncHostEl && (hostAndValueAreDifferentElements || !syncValueEl)) {
      this.$el.attr('value', formattedValue); // so the attribute updates in the DOM
    }
  }

  protected formatValue(value: DateTime): string {
    if (!value?.isValid) {
      return '';
    }

    return value.toFormat(DateUtil.DATE_FORMAT_PYTHON_AWARE);
  }

  protected updateValidityState(value: DateTime): DateTime {
    if (!value) {
      if (this.required) {
        this.valueEl.setCustomValidity('required');
      }
      return value;
    }

    if (!value.isValid) {
      this.valueEl.setCustomValidity(VALIDITY_INVALID_DATETIME);
      return value;
    }

    return value;
  }

  /**
   * Handles UI side effects of value updates, such as validation display and change events
   */
  protected onValue(value: DateTime, isChanged: boolean, isValid: boolean): void {
    this.syncPlaceholder(this.disabled);
    this.$el.toggleClass('is-invalid', !isValid);

    if (isChanged) {
      this.$el.trigger('change');
    }
  }

  protected invalidDateTimeAttributeError(attrName: string, value: DateTime): AttributeInvalidError | AttributeRequiredError {
    if (value) {
      return this.attributeInvalidError(attrName, value.invalidExplanation, value);
    }
    return this.requiredAttributeError(attrName);
  }

}
